import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { apiFetch, ApiError } from "@/lib/api";
import {
  formatTimestamp,
  type DocumentCollectionSkusResponse,
  type DocumentSkuOption,
  type ProductCollection,
  type ProductCollectionRelation,
} from "@/lib/documents";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { SkuSearchableSelect } from "@/components/mrp/SkuSearchableSelect";
import { DocumentModal } from "./DocumentModal";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

const QUERY_KEY = ["document-collections"] as const;

function formatHierarchyRelationship(
  currentCollectionId: string,
  relationship: ProductCollectionRelation,
): string {
  if (relationship.relation_type === "part_of") {
    if (relationship.from_collection_id === currentCollectionId) {
      return `Child of ${relationship.to_collection_code} · ${relationship.to_collection_name}`;
    }
    if (relationship.to_collection_id === currentCollectionId) {
      return `Parent of ${relationship.from_collection_code} · ${relationship.from_collection_name}`;
    }
  }
  return `${relationship.from_collection_code} ${relationship.relation_type.replaceAll("_", " ")} ${relationship.to_collection_code}`;
}

function slugifyName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function DocumentCollectionsPage() {
  const queryClient = useQueryClient();
  const hubDocumentSettingsAllowed = useHubModuleAdmin("documents");
  const documentsHubSettingsAdminTitle =
    "Module administrator access is required to change Document Control hub settings.";
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editBomTemplateId, setEditBomTemplateId] = useState("");
  const [skuQuery, setSkuQuery] = useState("");
  const [selectedSku, setSelectedSku] = useState("");
  const [memberNotes, setMemberNotes] = useState("");
  const [parentCollectionIds, setParentCollectionIds] = useState<string[]>([]);
  const [childCollectionIds, setChildCollectionIds] = useState<string[]>([]);
  const [relationNotes, setRelationNotes] = useState("");

  const collectionsQuery = useQuery<{ collections: ProductCollection[] }, ApiError>({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<{ collections: ProductCollection[] }>("/api/document-collections"),
  });

  const bomTemplateOptionsQuery = useQuery<{ templates: { value: number; label: string }[] }, ApiError>({
    queryKey: [...QUERY_KEY, "bom-template-options"],
    queryFn: () =>
      apiFetch<{ templates: { value: number; label: string }[] }>("/api/document-collections/bom-template-options"),
    enabled: collectionsQuery.isSuccess && (collectionsQuery.data?.collections.length ?? 0) > 0,
  });

  const skuQueryResult = useQuery<{ skus: DocumentSkuOption[] }>({
    queryKey: ["documents", "collection-sku-search", skuQuery],
    queryFn: () => apiFetch<{ skus: DocumentSkuOption[] }>("/api/document-skus", { query: { q: skuQuery } }),
    enabled: selectedCollectionId !== "",
  });

  const collectionSkusQuery = useQuery<DocumentCollectionSkusResponse, ApiError>({
    queryKey: ["document-collections", selectedCollectionId, "skus"],
    queryFn: () => apiFetch<DocumentCollectionSkusResponse>(`/api/document-collections/${selectedCollectionId}/skus`),
    enabled: selectedCollectionId !== "",
  });

  const relationshipsQuery = useQuery<{ relationships: ProductCollectionRelation[] }>({
    queryKey: ["document-collections", selectedCollectionId, "relationships"],
    queryFn: () =>
      apiFetch<{ relationships: ProductCollectionRelation[] }>(
        `/api/document-collections/${selectedCollectionId}/relationships`,
      ),
    enabled: selectedCollectionId !== "",
  });

  const collections = collectionsQuery.data?.collections ?? [];
  const selectedCollection = collections.find((collection) => collection.id === selectedCollectionId) ?? null;
  const skuOptions = useMemo(
    () =>
      (skuQueryResult.data?.skus ?? []).map((sku) => ({
        value: sku.sku,
        label: `${sku.sku}${sku.product_name ? ` - ${sku.product_name}` : ""}`,
      })),
    [skuQueryResult.data?.skus],
  );
  const relationshipOptions = useMemo(
    () => collections.filter((collection) => collection.id !== selectedCollectionId),
    [collections, selectedCollectionId],
  );

  const bomTemplateIdFieldInvalid = useMemo(() => {
    const trimmed = editBomTemplateId.trim();
    if (trimmed === "") return false;
    return !/^[1-9][0-9]*$/.test(trimmed);
  }, [editBomTemplateId]);

  useEffect(() => {
    if (!slugEdited) setSlug(slugifyName(name));
  }, [name, slugEdited]);

  useEffect(() => {
    if (!selectedCollection) return;
    setEditSlug(selectedCollection.code);
    setEditName(selectedCollection.name);
    setEditDescription(selectedCollection.description ?? "");
    setEditBomTemplateId(
      selectedCollection.bom_template_id != null ? String(selectedCollection.bom_template_id) : "",
    );
  }, [selectedCollection]);

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ collection: ProductCollection }>("/api/document-collections", {
        method: "POST",
        body: {
          slug: slug.trim(),
          name: name.trim(),
          description: description.trim() || null,
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents", "lookups"] });
      setSelectedCollectionId(result.collection.id);
      setSlug("");
      setName("");
      setDescription("");
      setSlugEdited(false);
      setAddOpen(false);
    },
  });

  const updateCollectionMutation = useMutation({
    mutationFn: () => {
      const trimmedBom = editBomTemplateId.trim();
      let bomTemplateId: number | null = null;
      if (trimmedBom !== "") {
        const parsed = Number.parseInt(trimmedBom, 10);
        if (!Number.isInteger(parsed) || parsed < 1) {
          throw new Error("BoM template must be a positive integer or left blank.");
        }
        bomTemplateId = parsed;
      }
      return apiFetch<{ collection: ProductCollection }>(`/api/document-collections/${selectedCollectionId}`, {
        method: "PATCH",
        body: {
          slug: editSlug.trim(),
          name: editName.trim(),
          description: editDescription.trim() || null,
          bomTemplateId,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["documents", "lookups"] });
    },
  });

  const addSkuMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/api/document-collections/${selectedCollectionId}/skus`, {
        method: "POST",
        body: { sku: selectedSku, notes: memberNotes.trim() || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "skus"] });
      setSelectedSku("");
      setMemberNotes("");
    },
  });

  const removeSkuMutation = useMutation({
    mutationFn: (sku: string) =>
      apiFetch<void>(`/api/document-collections/${selectedCollectionId}/skus`, {
        method: "DELETE",
        query: { sku },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "skus"] });
    },
  });

  const addRelationshipMutation = useMutation({
    mutationFn: async () => {
      const requests: Array<Promise<{ relationship: ProductCollectionRelation }>> = [];
      const notes = relationNotes.trim() || null;
      if (parentCollectionIds.length === 0 && childCollectionIds.length === 0) {
        throw new Error("Choose at least one parent or child collection.");
      }
      for (const parentCollectionId of parentCollectionIds) {
        requests.push(
          apiFetch<{ relationship: ProductCollectionRelation }>(
            `/api/document-collections/${selectedCollectionId}/relationships`,
            {
              method: "POST",
              body: {
                toCollectionId: parentCollectionId,
                relationType: "part_of",
                notes,
              },
            },
          ),
        );
      }
      for (const childCollectionId of childCollectionIds) {
        requests.push(
          apiFetch<{ relationship: ProductCollectionRelation }>(
            `/api/document-collections/${childCollectionId}/relationships`,
            {
              method: "POST",
              body: {
                toCollectionId: selectedCollectionId,
                relationType: "part_of",
                notes,
              },
            },
          ),
        );
      }
      return Promise.all(requests);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "relationships"] });
      setParentCollectionIds([]);
      setChildCollectionIds([]);
      setRelationNotes("");
    },
  });

  const addTemplateSkuExclusionMutation = useMutation({
    mutationFn: (sku: string) =>
      apiFetch<{ ok: true }>(`/api/document-collections/${selectedCollectionId}/template-sku-exclusions`, {
        method: "POST",
        body: { sku, notes: null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "skus"] });
    },
  });

  const removeTemplateSkuExclusionMutation = useMutation({
    mutationFn: (sku: string) =>
      apiFetch<void>(
        `/api/document-collections/${selectedCollectionId}/template-sku-exclusions`,
        { method: "DELETE", query: { sku } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "skus"] });
    },
  });

  const removeRelationshipMutation = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/document-collection-relationships/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-collections", selectedCollectionId, "relationships"] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitleWithHelp hoverHint="Collections group part numbers and documents for customer-facing context without defining manufacturing interchangeability.">
            Product collections
          </PageTitleWithHelp>
        </div>
        <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle}>
        <HoverHintTrigger
          trigger={
            <Button
              type="button"
              variant="primary"
              disabled={!hubDocumentSettingsAllowed}
              onClick={() => setAddOpen(true)}
            >
              Add collection
            </Button>
          }
        >
          Create a new product collection (slug, name, and optional description) for grouping part numbers and documents.
        </HoverHintTrigger>
        </ReadOnlyHoverLayer>
      </header>

      {addOpen ? (
        <DocumentModal title="Add product collection" onClose={() => setAddOpen(false)} showCloseButton={false}>
          <div className="space-y-4">
            <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle} layout="block">
            <fieldset
              disabled={!hubDocumentSettingsAllowed || createMutation.isPending}
              className="border-0 p-0"
            >
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm font-medium text-slate-700">
                Slug
                <Input
                  className="mt-1"
                  value={slug}
                  onChange={(event) => {
                    setSlugEdited(true);
                    setSlug(event.target.value);
                  }}
                  placeholder="fi-3c"
                />
                <span className="mt-1 block text-xs text-slate-500">Short display label for navigation and lists.</span>
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Name
                <Input className="mt-1" value={name} onChange={(event) => setName(event.target.value)} />
              </label>
              <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                Description
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={2}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </label>
            </div>
            </fieldset>
            </ReadOnlyHoverLayer>
            {createMutation.isError ? (
              <p className="text-sm text-red-600">{(createMutation.error as Error).message}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <HoverHintTrigger
                trigger={
                  <Button type="button" variant="ghost" disabled={createMutation.isPending} onClick={() => setAddOpen(false)}>
                    Cancel
                  </Button>
                }
              >
                Discard this form and close without creating a collection.
              </HoverHintTrigger>
              <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle}>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    variant="primary"
                    disabled={
                      createMutation.isPending || slug.trim() === "" || name.trim() === "" || !hubDocumentSettingsAllowed
                    }
                    onClick={() => createMutation.mutate()}
                  >
                    Add collection
                  </Button>
                }
              >
                Save the new collection with the slug, name, and description you entered.
              </HoverHintTrigger>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Collections</CardTitle>
          </CardHeader>
          <CardBody>
            {collectionsQuery.isError ? (
              <p className="text-sm text-red-600">{collectionsQuery.error.message}</p>
            ) : collectionsQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading...</p>
            ) : collections.length === 0 ? (
              <p className="text-sm text-slate-500">No product collections yet.</p>
            ) : (
              <div className="space-y-2">
                {collections.map((collection) => (
                  <HoverHintTrigger
                    key={collection.id}
                    className="block w-full"
                    trigger={
                      <button
                        type="button"
                        className={`block w-full rounded-lg border p-3 text-left text-sm ${
                          selectedCollectionId === collection.id
                            ? "border-brand-300 bg-brand-50"
                            : "border-slate-200 bg-white hover:bg-slate-50"
                        }`}
                        onClick={() => setSelectedCollectionId(collection.id)}
                      >
                        <div className="font-medium text-slate-900">
                          {collection.code} - {collection.name}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Updated {formatTimestamp(collection.updated_at)}</div>
                        {collection.description ? <div className="mt-1 text-slate-600">{collection.description}</div> : null}
                      </button>
                    }
                  >
                    Select this collection to edit slug, name, member part numbers, and relationships on the right.
                  </HoverHintTrigger>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{selectedCollection ? selectedCollection.name : "Collection details"}</CardTitle>
          </CardHeader>
          <CardBody>
            {!selectedCollection ? (
              <p className="text-sm text-slate-500">Choose a collection to manage member part numbers and relationships.</p>
            ) : (
              <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle} layout="block">
              <fieldset
                disabled={!hubDocumentSettingsAllowed}
                className="space-y-6 border-0 p-0 min-w-0"
              >
              <div className="space-y-6">
                <section>
                  <h2 className="text-sm font-semibold text-slate-900">Collection details</h2>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Slug
                      <Input className="mt-1" value={editSlug} onChange={(event) => setEditSlug(event.target.value)} />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Name
                      <Input className="mt-1" value={editName} onChange={(event) => setEditName(event.target.value)} />
                    </label>
                    <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                      Description
                      <textarea
                        className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                        rows={2}
                        value={editDescription}
                        onChange={(event) => setEditDescription(event.target.value)}
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                      BoM template (optional)
                      <select
                        className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                        value={editBomTemplateId}
                        onChange={(event) => setEditBomTemplateId(event.target.value)}
                        disabled={bomTemplateOptionsQuery.isLoading}
                      >
                        <option value="">No template (explicit members only)</option>
                        {(bomTemplateOptionsQuery.data?.templates ?? []).map((template) => (
                          <option key={template.value} value={String(template.value)}>
                            {template.label}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 block text-xs text-slate-500">
                        Every part number that uses this bill-of-materials template is treated as a member automatically. Use
                        exclusions below to omit specific part numbers. Explicit members are always included.
                      </span>
                    </label>
                  </div>
                  {updateCollectionMutation.isError ? (
                    <p className="mt-2 text-sm text-red-600">{(updateCollectionMutation.error as Error).message}</p>
                  ) : null}
                  <div className="mt-3 flex justify-end">
                    <HoverHintTrigger
                      trigger={
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={
                            updateCollectionMutation.isPending ||
                            editSlug.trim() === "" ||
                            editName.trim() === "" ||
                            bomTemplateIdFieldInvalid
                          }
                          onClick={() => updateCollectionMutation.mutate()}
                        >
                          Save collection
                        </Button>
                      }
                    >
                      Persist slug, name, description, and BoM template link for the selected collection.
                    </HoverHintTrigger>
                  </div>
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-slate-900">Member part numbers</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Merged list: explicit members plus any part numbers that currently use the linked BoM template (unless
                    excluded).
                  </p>
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto]">
                    <SkuSearchableSelect
                      value={selectedSku}
                      onValueChange={(option) => setSelectedSku(option?.value ?? "")}
                      options={skuOptions}
                      searchValue={skuQuery}
                      onSearchChange={setSkuQuery}
                      placeholder="Choose part number / SKU"
                      searchPlaceholder="Search SKU"
                      emptyMessage={skuQuery.trim() ? "No SKUs found." : "Type to search SKUs."}
                      isLoading={skuQueryResult.isFetching}
                      selectedLabelOverride={selectedSku}
                    />
                    <HoverHintTrigger
                      trigger={
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={selectedSku === "" || addSkuMutation.isPending}
                          onClick={() => addSkuMutation.mutate()}
                        >
                          Add member
                        </Button>
                      }
                    >
                      Link the chosen part number to this collection, with optional membership notes.
                    </HoverHintTrigger>
                  </div>
                  <Input
                    className="mt-2"
                    value={memberNotes}
                    onChange={(event) => setMemberNotes(event.target.value)}
                    placeholder="Optional membership notes"
                  />
                  <div className="mt-3 space-y-2">
                    {(collectionSkusQuery.data?.skus ?? []).map((sku) => (
                      <div key={sku.sku} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 p-2 text-sm">
                        <span className="min-w-0">
                          <span className="font-medium text-slate-900">{sku.sku}</span>
                          {sku.product_name ? <span className="text-slate-600"> - {sku.product_name}</span> : null}
                          <span className="ml-2 inline-block rounded bg-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-700">
                            {sku.membership_source === "template" ? "BoM template" : "Explicit"}
                          </span>
                        </span>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={
                                sku.membership_source === "explicit"
                                  ? removeSkuMutation.isPending
                                  : addTemplateSkuExclusionMutation.isPending
                              }
                              onClick={() =>
                                sku.membership_source === "explicit"
                                  ? removeSkuMutation.mutate(sku.sku)
                                  : addTemplateSkuExclusionMutation.mutate(sku.sku)
                              }
                            >
                              {sku.membership_source === "explicit" ? "Remove" : "Exclude from template"}
                            </Button>
                          }
                        >
                          {sku.membership_source === "explicit"
                            ? "Remove this explicit member from the collection."
                            : "Hide this part number from template-driven membership (does not delete the part number)."}
                        </HoverHintTrigger>
                      </div>
                    ))}
                  </div>
                  {(collectionSkusQuery.data?.template_sku_exclusions ?? []).length > 0 ? (
                    <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/80 p-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                        Excluded from BoM template membership
                      </h3>
                      <ul className="mt-2 space-y-2 text-sm">
                        {(collectionSkusQuery.data?.template_sku_exclusions ?? []).map((row) => (
                          <li key={row.sku} className="flex flex-wrap items-center justify-between gap-2">
                            <span>
                              <span className="font-mono font-medium text-slate-900">{row.sku}</span>
                              {row.product_name ? (
                                <span className="text-slate-600"> — {row.product_name}</span>
                              ) : null}
                            </span>
                            <HoverHintTrigger
                              trigger={
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  disabled={removeTemplateSkuExclusionMutation.isPending}
                                  onClick={() => removeTemplateSkuExclusionMutation.mutate(row.sku)}
                                >
                                  Undo exclusion
                                </Button>
                              }
                            >
                              Allow this part number to match the linked BoM template again (if it still uses that
                              template).
                            </HoverHintTrigger>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-slate-900">Parent and child collections</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Hold Control or Command to select more than one. This only creates nesting (
                    <span className="font-mono">part_of</span>) links, not custom relationship types.
                  </p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Child of (parents)
                      <select
                        multiple
                        className="mt-1 min-h-28 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                        value={parentCollectionIds}
                        onChange={(event) =>
                          setParentCollectionIds(Array.from(event.target.selectedOptions, (option) => option.value))
                        }
                      >
                        {relationshipOptions.map((collection) => (
                          <option key={collection.id} value={collection.id}>
                            {collection.code} - {collection.name}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 block text-xs text-slate-500">
                        This collection becomes a child of each selected parent (this collection is{" "}
                        <span className="font-mono">part_of</span> the parent).
                      </span>
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Parent of (children)
                      <select
                        multiple
                        className="mt-1 min-h-28 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                        value={childCollectionIds}
                        onChange={(event) =>
                          setChildCollectionIds(Array.from(event.target.selectedOptions, (option) => option.value))
                        }
                      >
                        {relationshipOptions.map((collection) => (
                          <option key={collection.id} value={collection.id}>
                            {collection.code} - {collection.name}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 block text-xs text-slate-500">
                        Each selected collection becomes a child of this collection (child is{" "}
                        <span className="font-mono">part_of</span> this one).
                      </span>
                    </label>
                  </div>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="min-w-[12rem] flex-1">
                      <Input
                        value={relationNotes}
                        onChange={(event) => setRelationNotes(event.target.value)}
                        placeholder="Optional relationship notes"
                      />
                    </div>
                    <HoverHintTrigger
                      trigger={
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={
                            (parentCollectionIds.length === 0 && childCollectionIds.length === 0) ||
                            addRelationshipMutation.isPending
                          }
                          onClick={() => addRelationshipMutation.mutate()}
                        >
                          Add hierarchy links
                        </Button>
                      }
                    >
                      Create <span className="font-mono">part_of</span> relationships for the selected parents and
                      children.
                    </HoverHintTrigger>
                  </div>
                  <div className="mt-3 space-y-2">
                    {(relationshipsQuery.data?.relationships ?? []).map((relationship) => (
                      <div key={relationship.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 p-2 text-sm">
                        <span>
                          <span className="font-medium text-slate-900">
                            {formatHierarchyRelationship(selectedCollection.id, relationship)}
                          </span>
                          {relationship.notes ? <span className="text-slate-600"> — {relationship.notes}</span> : null}
                          {relationship.relation_type !== "part_of" ? (
                            <span className="ml-2 text-xs text-amber-700">(legacy type: {relationship.relation_type})</span>
                          ) : null}
                        </span>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={removeRelationshipMutation.isPending}
                              onClick={() => removeRelationshipMutation.mutate(relationship.id)}
                            >
                              Remove
                            </Button>
                          }
                        >
                          Delete this collection-to-collection relationship.
                        </HoverHintTrigger>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
              </fieldset>
              </ReadOnlyHoverLayer>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
